import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from '@shared/config/axios';
import { ORDER_STATUSES } from '@shared/api/constants';
import { Box, Card, CardContent, Container, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import { Alert } from '@features/alert';
import { TypographyOrderStatus } from '@features/typography-colored-status-order';
import { LoadingPage } from '@widgets/loading-page';

export const OrdersViewPage = () => {
    const alertRef = useRef();

    const [ordersData, setOrdersData] = useState([]);
    // allocationResult будет словарём, где ключ – id заказа,
    // а значение – объект с информацией по распределению упаковок
    const [allocationResult, setAllocationResult] = useState({});

    const lastSelectedOrderId = localStorage.getItem('lastSelectedOrderId');

    const { data: orders, isLoading } = useQuery({
        queryKey: ['order'],
        queryFn: () =>
            axios
                .get('/orders/', { params: { status: ORDER_STATUSES.RECEIVED } })
                .then(({ data }) => data),
        refetchOnWindowFocus: true,
        staleTime: 0,
        cacheTime: 0,
    });

    const { data: completedPacks } = useQuery({
        queryKey: ['completed-packs'],
        queryFn: () =>
            axios.get('/packs/', { params: { status: 'completed' } }).then(res => res.data),
        refetchOnWindowFocus: true,
    });

    // Функция для формирования ключа упаковки
    const getPackKey = (pack) =>
        `${pack.weight}_${pack.grind}_${pack.roastType._id}_${pack.country._id}_${pack.producerName}`;

    // Функция распределения упаковок между заказами.
    // На вход получает список заказов и список доступных упаковок.
    // Для каждого заказа рассчитывается, сколько упаковок можно выделить.
    const distributePacks = (orders, availablePacks) => {
        // Создаём словарь инвентаря: ключ – getPackKey, значение – количество упаковок этого типа
        const inventory = {};
        availablePacks.forEach(pack => {
            const key = getPackKey(pack);
            inventory[key] = (inventory[key] || 0) + 1;
        });

        const allocation = {};

        orders.forEach(order => {
            // Инициализация информации по распределению для текущего заказа
            const orderAlloc = {
                allocated: 0,        // общее количество выделенных упаковок
                total: order.packs.length, // сколько упаковок требуется по заказу
                details: []          // по каждому типу упаковки: { key, allocated, required }
            };

            // Подсчёт требуемых упаковок по типу для данного заказа
            const requiredCounts = {};
            order.packs.forEach(pack => {
                const key = getPackKey(pack);
                requiredCounts[key] = (requiredCounts[key] || 0) + 1;
            });

            // Распределяем упаковки из инвентаря по каждому требуемому типу
            Object.keys(requiredCounts).forEach(key => {
                const available = inventory[key] || 0;
                const required = requiredCounts[key];
                // Можно выделить максимум столько упаковок, сколько есть в наличии
                const allocatedForKey = Math.min(available, required);
                orderAlloc.allocated += allocatedForKey;
                // Уменьшаем количество доступных упаковок для данного ключа
                inventory[key] = available - allocatedForKey;
                orderAlloc.details.push({ key, allocated: allocatedForKey, required });
            });
            // Флаг, показывающий, что заказ может быть полностью упакован
            orderAlloc.isFullyPackable = orderAlloc.allocated === orderAlloc.total;
            allocation[order._id] = orderAlloc;
        });

        return allocation;
    };

    // Когда данные о заказах и упаковках загружены, выполняем перераспределение и сортировку заказов
    useEffect(() => {
        if (orders && completedPacks) {
            const allocation = distributePacks(orders, completedPacks);
            setAllocationResult(allocation);

            // Сортировка заказов:
            // - Сначала полностью пакуемые (isFullyPackable true)
            // - Затем частично пакуемые (есть хоть одна выделенная упаковка)
            // - Затем те, для которых ни одной упаковки выделено
            const sortedOrders = [...orders].sort((a, b) => {
                const aAlloc = allocation[a._id];
                const bAlloc = allocation[b._id];
                const aScore = aAlloc.isFullyPackable ? 2 : (aAlloc.allocated > 0 ? 1 : 0);
                const bScore = bAlloc.isFullyPackable ? 2 : (bAlloc.allocated > 0 ? 1 : 0);
                return bScore - aScore;
            });
            setOrdersData(sortedOrders);
        }
    }, [orders, completedPacks]);

    // Функция форматирования даты
    const formatDate = (isoString) => {
        const date = new Date(isoString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${day}-${month}-${year}`;
    };

    if (isLoading) return <LoadingPage />;

    return (
        <>
            <Container
                className="no-scrollbar"
                sx={{
                    padding: 2,
                    height: '100%',
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'flex-start',
                    flexWrap: 'wrap',
                    overflowY: 'auto'
                }}
            >
                <Box
                    sx={{
                        width: '100%',
                        rowGap: 2,
                        columnGap: 4,
                        display: 'flex',
                        flexWrap: 'wrap',
                        justifyContent: 'flex-start',
                    }}
                >
                    {ordersData.map((order) => {
                        // Получаем информацию о распределении для заказа
                        const allocation = allocationResult[order._id] || { allocated: 0, total: order.packs.length };

                        return (
                            <Card
                                key={order?._id}
                                component={allocation.allocated > 0 ? Link : 'div'}
                                to={allocation.allocated > 0 ? `../orders/${order?._id}` : undefined}
                                sx={{
                                    pointerEvents: allocation.allocated > 0 ? 'auto' : 'none',
                                    opacity: allocation.allocated > 0 ? 1 : 0.5,
                                    textDecoration: 'none',
                                    borderRadius: '12px',
                                    width: { xs: '100%', sm: '200px' },
                                    alignSelf: 'flex-start',
                                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                    '&:hover': allocation.allocated > 0
                                        ? {
                                            transform: 'scale(1.05)',
                                            boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.2)',
                                        }
                                        : {},
                                }}
                            >
                                <CardContent>
                                    <Box display="flex" justifyContent="space-between" sx={{ borderBottom: '1px solid #ccc', pb: 2 }}>
                                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                            order: {order?.uuid.slice(0, 6)}
                                        </Typography>
                                    </Box>
                                    <Box display="flex" justifyContent="space-between" sx={{ pt: 1 }}>
                                        <TypographyOrderStatus
                                            status={order?.status}
                                            variant="body1"
                                            sx={{ fontWeight: 'bold' }}
                                        />
                                    </Box>
                                    <Box display="flex" justifyContent="space-between" sx={{ pt: 1 }}>
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                            {order?.created_at ? formatDate(order.created_at) : ''}
                                        </Typography>
                                        <Typography variant="body1" color="success.main" sx={{ fontWeight: 'bold' }}>
                                            {order?._id === lastSelectedOrderId ? 'last' : ''}
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Card>
                        );
                    })}
                </Box>
            </Container>
            <Alert ref={alertRef} />
        </>
    );
};
